import { useEffect, useState, useCallback, useRef } from "react";
import { Navigate, Link } from "react-router-dom";
import { RefreshCw, Lock, User, ShieldCheck, Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { LOGIN, CAPTCHA } from "@/constants/testIds";

const Field = ({ id, label, icon: Icon, ...props }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="overline-label">{label}</Label>
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input id={id} className="h-11 pl-10 bg-white" {...props} />
    </div>
  </div>
);

export default function Login() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ username: "", password: "", answer: "" });
  const [captcha, setCaptcha] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reqId = useRef(0);
  const loadCaptcha = useCallback(async () => {
    const id = ++reqId.current;
    setCaptcha(null);
    setForm((f) => ({ ...f, answer: "" }));
    const { data } = await api.get("/auth/captcha");
    if (id === reqId.current) setCaptcha(data);
  }, []);

  useEffect(() => {
    loadCaptcha();
  }, [loadCaptcha]);

  if (user) return <Navigate to="/balasan" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", {
        username: form.username, password: form.password,
        captcha_id: captcha?.captcha_id || "", captcha_answer: form.answer,
      });
      setUser(data);
    } catch (err) {
      setError(errMsg(err));
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] bg-white">
      <div className="flex flex-col justify-between px-6 py-10 sm:px-12 lg:px-20">
        <Link to="/" className="self-start fade-up" data-testid="login-home-link"><img src="/logo.png" alt="Arumichi Travel" className="h-12 w-auto" /></Link>
        <form onSubmit={submit} className="w-full max-w-md space-y-6 py-12 fade-up" style={{ animationDelay: "80ms" }}>
          <div className="space-y-3">
            <p className="overline-label text-brand">Portal Internal</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-none">Masuk ke akun Anda</h1>
            <p className="text-sm text-slate-500 leading-relaxed">Gunakan akun yang diberikan oleh admin Arumichi Travel.</p>
          </div>
          <Field id="username" label="Username" icon={User} value={form.username} onChange={set("username")} autoComplete="username" required data-testid={LOGIN.emailInput} />
          <Field id="password" label="Password" icon={Lock} type="password" value={form.password} onChange={set("password")} autoComplete="current-password" required data-testid={LOGIN.passwordInput} />
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <ShieldCheck className="h-4 w-4 text-brand" />
                <span data-testid={CAPTCHA.question} className="font-medium">{captcha ? captcha.question : "Memuat captcha..."}</span>
              </div>
              <button type="button" onClick={loadCaptcha} className="rounded-md p-1.5 text-slate-500 hover:bg-white hover:text-brand transition-colors" aria-label="Ganti soal" data-testid={CAPTCHA.refresh}>
                <RefreshCw className="h-4 w-4" />
              </button>
            </div>
            <Input inputMode="numeric" placeholder="Jawaban" value={form.answer} onChange={set("answer")} required className="h-11 bg-white" data-testid={CAPTCHA.input} />
          </div>
          {error && <p className="text-sm text-red-600" data-testid={CAPTCHA.error}>{error}</p>}
          <Button type="submit" disabled={loading || !captcha} className="h-11 w-full rounded-full bg-brand hover:bg-brand-dark text-white font-medium" data-testid={LOGIN.submitButton}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
          </Button>
        </form>
        <p className="text-xs text-slate-400">© {new Date().getFullYear()} Arumichi Travel</p>
      </div>
      <div className="relative hidden lg:block overflow-hidden">
        <img src="/login-bg.jpg" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <p className="overline-label text-white/80">Arumichi Travel</p>
          <p className="font-heading text-3xl font-semibold tracking-tight mt-3 max-w-md">Satu tempat untuk semua jawaban tim CS.</p>
        </div>
      </div>
    </div>
  );
}
